/**
 * Hierarchy Data Hooks
 *
 * TanStack Query hooks for fetching the sidebar tree hierarchy.
 * Automatically refreshes when WebSocket events invalidate the cache.
 */

import { useQuery } from '@tanstack/react-query';
import { apiService } from '../../services/api.service';
import { queryKeys } from '../../lib/queryClient';
import { useWorkspaceId } from '../../contexts/WorkspaceContext';
import type { HierarchyNode } from '@bower/shared/types/api';
import { getHierarchyChildrenPage } from '../../services/api/hierarchy.api';

/**
 * Fetch the first page of root hierarchy nodes (folders plus workspace-level
 * files) for the current workspace. `data.nextCursor` is set while more root
 * files remain; the tree appends later pages into this same cache entry.
 */
export function useHierarchyRoots() {
  const workspaceId = useWorkspaceId();

  return useQuery({
    queryKey: queryKeys.hierarchy.root(workspaceId || ''),
    queryFn: () => getHierarchyChildrenPage(apiService, { parentType: 'workspace' }),
    enabled: !!workspaceId,
    // Hierarchy data can be slightly stale since WebSocket handles updates
    staleTime: 60 * 1000,
  });
}

/**
 * Fetch the user's "Shared with me" list for the current workspace.
 * Returns entities the user can access whose parent is inaccessible —
 * the orphan set rendered as a flat section above the regular tree.
 *
 * Plan: docs/planning/2026-04-25-entity-level-permissions.md §4.
 */
export function useSharedWithMe() {
  const workspaceId = useWorkspaceId();

  return useQuery({
    queryKey: queryKeys.hierarchy.sharedWithMe(workspaceId || ''),
    queryFn: async () => {
      const response = await apiService.get<HierarchyNode[]>('/hierarchy/shared-with-me');
      if (!response.success) {
        throw new Error(response.error?.message || 'Failed to fetch shared-with-me');
      }
      return response.data as HierarchyNode[];
    },
    enabled: !!workspaceId,
    // Slightly less stale than the regular tree — ACL changes are
    // rarer than ordinary edits.  Refetch on focus to catch updates
    // from other browser tabs / sessions where the user was just
    // granted access.
    staleTime: 60 * 1000,
    refetchOnWindowFocus: true,
  });
}

/**
 * The children query for one hierarchy node (its first page), run by the
 * workspace sidebar tree once per expanded folder through `useQueries`.
 */
export function hierarchyChildrenQueryOptions(
  parentType: 'workspace' | 'project',
  parentId: string | undefined,
) {
  return {
    queryKey: queryKeys.hierarchy.children(parentType, parentId || ''),
    queryFn: () => getHierarchyChildrenPage(apiService, { parentType, parentId }),
    enabled: !!parentId,
    staleTime: 60 * 1000,
  };
}
